import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Spin,
  Space,
  Typography,
  message,
} from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import { isSuperAdmin } from "../../../utils/permissions";
import { useAuth } from "../../../utils/useAuth";

const { Paragraph, Text } = Typography;

const ALLOWED_FEATURES = [
  "appointments",
  "calendar",
  "dashboard",
  "leaves",
  "overtime",
  "patientProfile",
  "patients",
  "business",
  "finance",
  "investments",
  "client-investments",
  "dailyRevenue",
  "payments",
  "schedules",
  "settings",
  "shifts",
  "support",
  "staff",
  "userProfile",
  "reservations",
  "apartments",
  "hierarchy",
  "credits",
  "clientInvestments",
  "suppliers",
];

const ClientFeatures = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const parsedAuthUser = useMemo(() => {
    if (!authUser) return undefined;
    if (typeof authUser === "string") {
      try {
        return JSON.parse(authUser);
      } catch (err) {
        console.error(err);
        return undefined;
      }
    }
    return authUser;
  }, [authUser]);

  const clientId = userData?.clientId || parsedAuthUser?.clientId;

  const featureOptions = useMemo(
    () =>
      ALLOWED_FEATURES.map((feature) => ({
        value: feature,
        label:
          {
            appointments: t("Appointments"),
            calendar: t("Calendar"),
            dashboard: t("Dashboard"),
            leaves: t("Leaves"),
            overtime: t("Overtime"),
            patientProfile: t("Patient Profile"),
            patients: t("Patients"),
            business: t("Business"),
            investments: t("Investments"),
            "client-investments": t("Client Investments"),
            payments: t("Payments"),
            schedules: t("Schedules"),
            settings: t("Settings"),
            shifts: t("Shifts"),
            support: t("Support"),
            staff: t("Staff"),
            userProfile: t("User Profile"),
            reservations: t("Reservations"),
            apartments: t("Apartments"),
            hierarchy: t("Hierarchy"),
            credits: t("Credits"),
            clientInvestments: t("Client Investments"),
            suppliers: t("Dobavljači"),
          }[feature] || feature,
      })),
    [t]
  );

  const loadClientFeatures = async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_API_URL}/client/${id}/features`);
      form.setFieldsValue({
        clientId: id,
        enabledFeatures: data?.enabledFeatures || [],
        type: data?.client || undefined,
      });
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load client features"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadClientFeatures(clientId);
    } else {
      form.resetFields();
    }
  }, [clientId]);

  const onFinish = async (values: any) => {
    if (!clientId) {
      message.error(t("Client ID is missing"));
      return;
    }

    const filteredFeatures = (values.enabledFeatures || []).filter((f: any) =>
      ALLOWED_FEATURES.includes(f)
    );

    setSaving(true);
    try {
      await axios.put(`${BASE_API_URL}/client/${clientId}/features`, {
        enabledFeatures: filteredFeatures,
        type: values.type,
      });
      message.success(t("Client features updated"));
    } catch (error) {
      console.error(error);
      message.error(t("Unable to update client features"));
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin(userData?.roles)) {
    return (
      <Alert
        type="warning"
        message={t("This section is available only to super admins.")}
        showIcon
      />
    );
  }

  return (
    <div className="settingsTab">
      <div className="settingsForm">
        <div style={{ marginBottom: 12 }}>
          <Paragraph style={{ marginBottom: 4 }}>
            <Text strong>{t("Manage what each client can see")}</Text>
          </Paragraph>
          <Text type="secondary">
            {t(
              "Pick the features visible to this client and optionally set their plan type."
            )}
          </Text>
        </div>

        {!clientId ? (
          <Alert
            type="warning"
            message={t("No client selected. Please refresh or log in again.")}
            showIcon
          />
        ) : (
          <Spin spinning={loading}>
            <Form
              layout="vertical"
              form={form}
              initialValues={{ enabledFeatures: [] }}
              onFinish={onFinish}
            >
              <Form.Item label={t("Client ID")} name="clientId">
                <Input disabled />
              </Form.Item>

              <Form.Item
                label={t("Enabled Features")}
                name="enabledFeatures"
                rules={[
                  {
                    type: "array",
                    message: t("Please pick at least one feature"),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder={t("Choose available features")}
                  options={featureOptions}
                  allowClear
                />
              </Form.Item>

              <Form.Item label={t("Client Type")} name="type">
                <Input placeholder={t("e.g. premium, basic, trial")} />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    disabled={loading}
                  >
                    {t("Save")}
                  </Button>
                  <Button onClick={() => loadClientFeatures(clientId)}>
                    {t("Refresh")}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        )}
      </div>
    </div>
  );
};

export default ClientFeatures;
